import * as React from 'react';
import { styled } from '@linaria/react';
import useSound from 'use-sound';

import { sampleOne } from '@/utils';

import VisuallyHidden from '@/components/VisuallyHidden';

const SPRITES: Record<string, [number, number]> = {
  down1: [250, 150],
  down2: [450, 150],
  down3: [650, 150],
  up1: [1050, 150],
  up2: [1250, 150],
  up3: [1450, 150],
};

interface Props extends React.HTMLAttributes<HTMLInputElement> {
  width: number;
  height: number;
  label: string;
  name: string;
  checked: boolean;
}

function GlowingPushButton({
  width,
  height,
  label,
  name,
  checked,
  ...delegated
}: Props) {
  const id = React.useId().replace(/:/g, '');

  const [playTick] = useSound('/sounds/rubber-button-sprite.mp3', {
    sprite: SPRITES,
    volume: 0.5,
  });

  return (
    <Wrapper
      data-mass={3}
      data-checked={String(checked)}
      style={{ width, height }}
      onPointerDown={() =>
        playTick({
          id: sampleOne(Object.keys(SPRITES).slice(0, 3)),
        })
      }
      onPointerUp={() =>
        playTick({ id: sampleOne(Object.keys(SPRITES).slice(3)) })
      }
    >
      <ButtonWell>
        <VisibleButton />
      </ButtonWell>
      <VisuallyHidden>
        <label htmlFor={id}>{label}</label>
      </VisuallyHidden>
      <ActualRadioButton
        type="radio"
        id={id}
        checked={checked}
        name={name}
        {...delegated}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  user-select: none;

  /*
    Outer glow.
    Using a pseudo-element rather than a box-shadow so that I can fade it in and out smoothly.
  */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: hsl(45deg 100% 59%);
    border-radius: 0px;
    filter: blur(3px);
    opacity: 0;
    transition: opacity 300ms;
  }

  &[data-checked='true']::before {
    opacity: 0.6;
    transition: opacity 50ms;
  }
`;

const ButtonWell = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: hsl(275deg 20% 16%);
  box-shadow: inset 0px 1px 2px hsl(275deg 20% 5% / 0.5);
  border-radius: 3px;

  ${Wrapper}[data-checked='true'] & {
    background: hsl(220deg 50% 30%);
  }
`;

const VisibleButton = styled.div`
  position: absolute;
  inset: 1px;
  width: calc(100% - 2px);
  height: calc(100% - 2px);
  border-radius: 2px;
  /* prettier-ignore */
  background:
    radial-gradient(
      147.45% 164.35% at 49.92% -64.35%,
      hsl(0deg 0% 0% / 0) 92%,
      hsl(0deg 0% 0% / 0.02) 94.03%,
      hsl(0deg 0% 0% / 0.06) 95.99%,
      hsl(0deg 0% 0% / 0.14) 98.01%,
      hsl(0deg 0% 0% / 0.2) 100%
    ),
    linear-gradient(
      92.49deg,
      hsl(0deg 0% 0% / 0.2) 1.57%,
      hsl(0deg 0% 0% / 0.08) 16.85%,
      hsl(0deg 0% 0% / 0) 33.3%,
      hsl(0deg 0% 0% / 0) 65.22%,
      hsl(0deg 0% 0% / 0.08) 82.03%,
      hsl(0deg 0% 0% / 0.2) 97.73%
    ),
    radial-gradient(
      55.48% 55.48% at 50% 50%,
      #bbb2c5 16.5%,
      #a399ab 50.5%,
      #908597 79.5%,
      #7d6e87 100%
    );
  box-shadow:
    inset 0px 2px 4px -1px hsl(0deg 0% 100% / 0.25),
    inset 0px 1px 2px -0.6px hsl(0deg 0% 100% / 0.4),
    inset 0px 2px 6px rgba(82, 46, 107, 0.5);
  transition: filter 300ms;

  ${Wrapper}:has(input:hover) & {
    filter: brightness(120%);
    transition: filter 50ms;
  }

  ${Wrapper}:has(input:active) & {
    filter: brightness(80%);
  }

  ${Wrapper}:has(input:focus-visible) & {
    outline: 2px solid white;
    outline-offset: 2px;
  }

  ${Wrapper}[data-checked='true'] & {
    background: radial-gradient(
      55.48% 55.48% at 50% 50%,
      hsl(45deg 100% 91%) 5.5%,
      hsl(44deg 100% 79%) 32.5%,
      hsl(42deg 100% 67%) 52%,
      hsl(39deg 78% 50%) 79.5%,
      hsl(35deg 91% 39%) 100%
    );
  }
`;

const ActualRadioButton = styled.input`
  position: absolute;
  opacity: 0;
  inset: -2px;
  width: calc(100% + 4px);
  height: calc(100% + 4px);
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
  margin: 0;
`;

export default GlowingPushButton;
